/**
 * The chrome every trial page shares: the app header and the global footer.
 *
 * Built once and reused, per the handoff's "Global components" table. The two
 * header variants differ enough to be a prop rather than two components: the
 * confirmation page has no navigation because there is nowhere to navigate yet.
 */

import React from 'react';
import { Link } from 'react-router-dom';

import './Trial.css';
import { ensureCampaignTag } from '../../analytics/campaignTag';

/**
 * The orange wordmark, on every background including the navy header.
 *
 * The handoff is explicit and repeats itself on this: "always in Core Orange",
 * "Never apply a filter, recolor or inversion". So there is no inverted variant
 * here to reach for by accident.
 */
function Logo({ className }) {
  return (
    <img
      src="/textOnlyLogoOrr-01.png"
      alt="Alchemiq"
      className={`tr-logo ${className || ''}`}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
}

/** Where "Contact us" goes. */
export const CONTACT_URL = 'https://www.alchemiq.ai/contact';
export const SITE_URL = 'https://www.alchemiq.ai';

/**
 * @param {'feed'|'refine'|null} active which nav item is current. The handoff:
 *   Feed is active on Feed, Story and Draft; Refine on Refine and Saved.
 */
export function TrialHeader({ token, active }) {
  if (!token) {
    return (
      <header className="tr-header tr-header--confirm">
        <div className="tr-wrap tr-wrap--shell tr-header-inner">
          <Logo className="tr-logo--confirm" />
        </div>
      </header>
    );
  }

  return (
    <header className="tr-header">
      <div className="tr-wrap tr-wrap--shell tr-header-inner">
        <Logo />
        <nav className="tr-nav">
          <Link
            to={`/trial/feed/${token}`}
            aria-current={active === 'feed' ? 'page' : undefined}
          >
            Your news feed
          </Link>
          <Link
            to={`/trial/feed/${token}/refine`}
            aria-current={active === 'refine' ? 'page' : undefined}
          >
            Refine your feed
          </Link>
        </nav>
      </div>
    </header>
  );
}

export function TrialFooter({ onContactClick, onSiteClick }) {
  return (
    <footer className="tr-footer">
      <div className="tr-wrap tr-wrap--shell tr-footer-inner">
        <div className="tr-footer-left">
          <Logo className="tr-logo--footer" />
          <span className="tr-meta">Questions or feedback about your feed?</span>
        </div>
        <div className="tr-footer-links">
          <a className="tr-link" href={CONTACT_URL} target="_blank" rel="noreferrer" onClick={onContactClick}>
            Contact us
          </a>
          {/* Opens a new tab, like every link that leaves the flow. Everything
              internal stays in this tab so Back keeps working. */}
          <a className="tr-link" href={SITE_URL} target="_blank" rel="noreferrer" onClick={onSiteClick}>
            Visit alchemiq.ai&nbsp;&#8599;
          </a>
        </div>
      </div>
    </footer>
  );
}

/**
 * The page wrapper.
 *
 * `.tr-page` is load bearing rather than decorative: `Trial.css` keys the
 * scroll-lock release off it, the same way the landing pages do, because the
 * app shell and html/body/#root are all overflow:hidden.
 */
export default function TrialShell({ token, active, children, onContactClick, onSiteClick }) {
  /*
   * Boot the marketing container for every trial page, during RENDER.
   *
   * `campaignAnalytics.track` also boots, which covers any page that fires an
   * event. That is not enough on its own: the confirmation and refine pages
   * fire nothing on view, only on interaction, so they loaded no container at
   * all and were invisible to any tag. Checked on staging before this line
   * existed, `/trial/confirmation` and `/trial/feed/<token>/refine` both
   * served zero container scripts. The refine link is in every email, so that
   * was a real path nobody could measure.
   *
   * During render, not in an effect, and that is the whole reason this is
   * here rather than in a `useEffect`. React runs CHILD effects before PARENT
   * effects, and `FeedPage` fires `full_feed_opened` from its own mount
   * effect, so a parent effect would push that event into `dataLayer` ahead
   * of the Consent Mode defaults. GTM replays the array in order, so a
   * default that lands after an event never applied to it. A parent's render
   * runs before every child effect, so the ordering holds. Same argument as
   * `campaignTag.js` makes for the landing tracker's `useMemo`.
   *
   * `ensureCampaignTag` is idempotent, so the double boot costs nothing.
   */
  ensureCampaignTag();

  return (
    <div className="tr-page">
      <TrialHeader token={token} active={active} />
      {children}
      <TrialFooter onContactClick={onContactClick} onSiteClick={onSiteClick} />
    </div>
  );
}
